import { createContext, useContext } from "react";
import type { PartnerOrgProfileResponse } from "@dearbill/shared";

/**
 * Value `OrgShell` publishes to everything under `/orgs/:orgId`: the one
 * org-profile response (the caller's `role`, the org `name`, computed `status`
 * and `blockers`) plus a setter. A successful profile save lifts the server's
 * updated response through `setRes`, so the org header, tab bar and status
 * banner re-render without a reload.
 *
 * The caller's own `GET /api/partner/me` record lives in the app-wide
 * `PartnerMeContext` instead, because the top bar, the org list and `/profile`
 * need it above the org subtree.
 */
export interface OrgContextValue {
  orgId: string;
  res: PartnerOrgProfileResponse;
  /** Lift the server's updated profile response (after a save). */
  setRes: (next: PartnerOrgProfileResponse) => void;
  isOrgAdmin: boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export const OrgContextProvider = OrgContext.Provider;

/** Read the org context; throws if used outside `OrgShell`. */
export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (ctx === null) {
    throw new Error("useOrg must be used within OrgShell");
  }
  return ctx;
}
